import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import { useState } from 'react';
import Link from '@fuse/core/Link';
import FuseSvgIcon from '@fuse/core/FuseSvgIcon';
import clsx from 'clsx';
import Popover from '@mui/material/Popover';
import type { PopoverProps } from '@mui/material/Popover';
import useUser from '@/auth/useUser';
import LucideIcon from '@/components/common/LucideIcon';
import { useSelectedOrganisation } from '@/contexts/SelectedOrganisationContext';
import { ALL_ORGANISATIONS } from '@/hooks/useOrganisationFilter';
import { usePermissions } from '@/hooks/usePermissions';
import { useSwitchableOrganisations } from '@/hooks/useOrganisations';
import { ChevronRight } from 'lucide-react';
import UserMenuDialog from './UserMenuDialog';

type UserMenuProps = {
	className?: string;
	popoverProps?: Partial<PopoverProps>;
	foldedandclosed?: boolean;
};

function getInitials(name: string) {
	if (!name) return 'U';

	const parts = name.trim().split(' ');

	if (parts.length === 1) return parts[0][0].toUpperCase();

	return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
}

function UserMenu(props: UserMenuProps) {
	const { className, popoverProps, foldedandclosed = false } = props;

	const { data: user, signOut, isGuest } = useUser();
	const { canChooseOrganisation } = usePermissions();
	const { organisations } = useSwitchableOrganisations(canChooseOrganisation);
	const { selectedOrganisationId } = useSelectedOrganisation();
	const [dialogOpen, setDialogOpen] = useState(false);
	const [userMenu, setUserMenu] = useState<HTMLElement | null>(null);

	// Only a GlobalAdmin has an organisation to switch, so only they get the account dialog.
	const userMenuClick = (event: React.MouseEvent<HTMLElement>) => {
		if (canChooseOrganisation) setDialogOpen(true);
		else setUserMenu(event.currentTarget);
	};

	const userMenuClose = () => {
		setUserMenu(null);
	};

	if (!user) {
		return null;
	}

	// A GlobalAdmin sees which organisation the app is showing; everyone else has only their own.
	const subtitle = canChooseOrganisation
		? selectedOrganisationId === ALL_ORGANISATIONS
			? 'All organisations'
			: (organisations.find((o) => String(o.id) === selectedOrganisationId)?.name ?? '')
		: user.email;

	const avatar = (
		<Avatar
			variant="square"
			sx={{
				bgcolor: 'var(--neutral-bg-active)',
				color: 'var(--primary-500)',
				width: 32,
				height: 32,
				borderRadius: '4px',
				fontWeight: 400,
				fontSize: 16,
			}}
		>
			{getInitials(String(user?.fullName || user?.displayName || ''))}
		</Avatar>
	);

	return (
		<>
			<Button
				className={clsx(
					'user-menu flex shrink-0 min-h-14 h-14 rounded-lg',
					foldedandclosed ? 'justify-center' : 'justify-start space-x-3 w-full',
					className,
				)}
				sx={(theme) => ({
					borderColor: theme.vars?.palette.divider,
					padding: 0,
				})}
				onClick={userMenuClick}
				color="inherit"
			>
				{avatar}

				{!foldedandclosed && (
					<>
						<div className="flex flex-col flex-auto space-y-2 min-w-0">
							<Typography
								component="span"
								className="title flex capitalize truncate leading-none"
								sx={{
									color: 'var(--neutral-dark-base)',
									fontSize: '16px',
									fontWeight: 500,
								}}
							>
								{user?.fullName}
							</Typography>
							<Typography
								className="subtitle leading-none truncate text-left"
								sx={{
									color: 'var(--neutral-gray-medium)',
									fontSize: '14px',
									fontWeight: 400,
								}}
							>
								{subtitle}
							</Typography>
						</div>
						<LucideIcon
							icon={ChevronRight}
							size={16}
							className="m-0"
							sx={{ color: 'var(--neutral-gray-dark)' }}
						/>
					</>
				)}
			</Button>

			<UserMenuDialog
				open={dialogOpen}
				onClose={() => setDialogOpen(false)}
			/>

			<Popover
				open={Boolean(userMenu)}
				anchorEl={userMenu}
				onClose={userMenuClose}
				anchorOrigin={{
					vertical: 'top',
					horizontal: 'center',
				}}
				transformOrigin={{
					vertical: 'bottom',
					horizontal: 'center',
				}}
				classes={{
					paper: 'py-2 min-w-64',
				}}
				{...popoverProps}
			>
				{isGuest ? (
					<>
						<MenuItem
							component={Link}
							to="/sign-in"
							role="button"
						>
							<ListItemIcon className="min-w-9">
								<FuseSvgIcon>heroicons-outline:lock-closed</FuseSvgIcon>
							</ListItemIcon>
							<ListItemText primary="Sign In" />
						</MenuItem>
						<MenuItem
							component={Link}
							to="/sign-up"
							role="button"
						>
							<ListItemIcon className="min-w-9">
								<FuseSvgIcon>heroicons-outline:user-plus</FuseSvgIcon>
							</ListItemIcon>
							<ListItemText primary="Sign up" />
						</MenuItem>
					</>
				) : (
					<MenuItem onClick={signOut}>
						<ListItemIcon className="min-w-9">
							<FuseSvgIcon>heroicons-outline:arrow-right-on-rectangle</FuseSvgIcon>
						</ListItemIcon>
						<ListItemText primary="Sign out" />
					</MenuItem>
				)}
			</Popover>
		</>
	);
}

export default UserMenu;
